import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "form", "value"]

  connect() {
    if (!this.hasFormTarget) return

    this.submitting = false
    this.boundSubmitEnd = this.submitEnd.bind(this)
    this.formTarget.addEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  disconnect() {
    if (!this.hasFormTarget) return

    this.formTarget.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  edit(event) {
    event.preventDefault()

    this.originalValue = this.fieldValue()
    this.displayTarget.classList.add("d-none")
    this.formTarget.classList.remove("d-none")

    const field = this.inputElement()
    field?.focus()
    if (field?.select && field?.type !== "select-one") {
      field.select()
    }
  }

  submitForm(event) {
    if (this.submitting) return

    event.preventDefault()
    this.submit()
  }

  commitInput(event) {
    if (event.key !== "Enter") return

    event.preventDefault()
    this.submit()
  }

  submit() {
    if (this.submitting) return

    const input = this.inputElement()
    if (input?.type === "checkbox") {
      this.submitting = true
      this.formTarget.requestSubmit()
      return
    }

    const currentValue = this.fieldValue()

    if (currentValue === this.originalValue) {
      this.showDisplay()
      return
    }

    this.updateDisplayValue(currentValue)
    this.submitting = true
    this.formTarget.requestSubmit()
  }

  submitEnd(event) {
    this.submitting = false

    if (this.flushResolve) {
      if (event.detail.success) {
        this.flushResolve()
      } else {
        this.flushReject?.()
      }

      this.flushResolve = null
      this.flushReject = null
    }

    if (event.detail.success) {
      this.originalValue = this.fieldValue()
      this.updateDisplayValue(this.fieldValue())
      this.showDisplay()
      return
    }

    if (this.hasValueTarget) {
      this.valueTarget.textContent = this.originalValue
    }

    this.showDisplay()
  }

  flushPendingChanges() {
    if (!this.hasFormTarget) {
      return Promise.resolve()
    }

    if (this.submitting) {
      return new Promise((resolve, reject) => {
        this.flushResolve = resolve
        this.flushReject = reject
      })
    }

    if (this.formTarget.classList.contains("d-none")) {
      return Promise.resolve()
    }

    const input = this.inputElement()
    if (input?.type === "checkbox") {
      return Promise.resolve()
    }

    const currentValue = this.fieldValue()

    if (currentValue === this.originalValue) {
      this.showDisplay()
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      this.flushResolve = resolve
      this.flushReject = reject
      this.submit()
    })
  }

  showDisplay() {
    if (!this.hasDisplayTarget) return

    this.formTarget.classList.add("d-none")
    this.displayTarget.classList.remove("d-none")
  }

  updateDisplayValue(value) {
    if (!this.hasValueTarget) return

    this.valueTarget.textContent = value || "—"
  }

  fieldValue() {
    return this.inputElement()?.value ?? ""
  }

  inputElement() {
    return this.formTarget.querySelector("input:not([type='hidden']), select, textarea")
  }
}
